// useBoardGroups.js

import { useMemo, useCallback } from 'react';

import { getOptionLabel, normalizeEnumOptionsViewSortMode, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import {
  getNodelistPrimaryKey,
  isNodelistSingleSelect,
} from 'utilities/NodelistField';
import { isRelationFieldType } from 'utilities/FieldTypes';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { normalizeBoardOrder, reconcileOrderedItems } from 'views/board/utils/boardOrder';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';

export const UNASSIGNED_KEY = '__UNASSIGNED__';
export const OTHER_KEY = '__OTHER__';

const orderRelationOptions = (options, config = {}) => {
  const hidden = new Set(
    Array.isArray(config.hiddenOptionIds)
      ? config.hiddenOptionIds.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
  );
  const orderIds = Array.isArray(config.optionOrderIds)
    ? config.optionOrderIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  const byId = new Map();
  (Array.isArray(options) ? options : []).forEach((option) => {
    const value = String(option?.value || '').trim();
    if (!value || hidden.has(value)) return;
    byId.set(value, {
      id: value,
      value,
      translations: { en: { label: String(option?.label || value) } },
      iconKey: String(option?.iconKey || '').trim(),
      color: '',
    });
  });

  const ordered = [];
  orderIds.forEach((id) => {
    if (!byId.has(id)) return;
    ordered.push(byId.get(id));
    byId.delete(id);
  });

  [...byId.values()]
    .sort((left, right) => getOptionLabel(left, 'en').localeCompare(getOptionLabel(right, 'en'), undefined, {
      sensitivity: 'base',
    }))
    .forEach((option) => ordered.push(option));

  return ordered;
};

const resolveBoardCellValue = (rawValue, boardFieldDef) => {
  if (isNodelistSingleSelect(boardFieldDef) || isRelationFieldType(boardFieldDef)) {
    return getNodelistPrimaryKey(rawValue);
  }

  if (rawValue == null) return '';
  const asString = String(rawValue);
  return asString.length ? asString : '';
};

const useBoardGroups = ({ view, typeDef, nodes = [], language = 'en', boardOrder = null, fieldValueOverrides = {} }) => {
  const boardFieldKey = view?.boardField || '';
  const boardEnumOptions = view?.boardEnumOptions && typeof view.boardEnumOptions === 'object'
    ? view.boardEnumOptions
    : null;

  const boardEnumOptionsConfig = useMemo(() => {
    const fieldKey = String(boardEnumOptions?.fieldKey || '').trim();
    if (!fieldKey || fieldKey !== boardFieldKey) {
      return {
        sortModeOverride: 'inherit',
        hiddenOptionIds: [],
        optionOrderIds: [],
      };
    }

    return {
      sortModeOverride: normalizeEnumOptionsViewSortMode(boardEnumOptions?.sortMode),
      hiddenOptionIds: Array.isArray(boardEnumOptions?.hiddenOptionIds)
        ? [...new Set(boardEnumOptions.hiddenOptionIds
          .map((value) => String(value || '').trim())
          .filter(Boolean))]
        : [],
      optionOrderIds: Array.isArray(boardEnumOptions?.optionOrderIds)
        ? [...new Set(boardEnumOptions.optionOrderIds
          .map((value) => String(value || '').trim())
          .filter(Boolean))]
        : [],
    };
  }, [boardEnumOptions, boardFieldKey]);

  const dataSectionKey = typeDef?.dataSection || '';

  const fields = useMemo(() => (
    resolveDisplaySchemaFields(typeDef, { includeHidden: true })
  ), [typeDef]);

  const boardFieldDef = useMemo(() => {
    if (!boardFieldKey) return null;
    return fields.find((field) => {
      const name = String(field?.name || '').trim();
      const key = String(field?.key || '').trim();
      return (name && name === boardFieldKey) || (key && key === boardFieldKey);
    }) || null;
  }, [fields, boardFieldKey]);

  const isNodelistBoard = isRelationFieldType(boardFieldDef);
  const targetType = String(boardFieldDef?.targetType || '').trim();

  const { options: relationOptions } = useNodelistTargetOptions({
    targetType,
    enabled: isNodelistBoard && Boolean(targetType),
  });

  const resolveFieldValue = useCallback((node, fieldDef) => {
    return resolveSchemaFieldValue({
      item: node,
      fieldDef,
      typeKey: typeDef?.key,
      dataSection: typeDef?.dataSection,
    });
  }, [typeDef?.dataSection, typeDef?.key]);

  const normalizedOptions = useMemo(() => {
    // Ignore legacy hiddenOptionIds — no editor manages them, and they can empty the board
    // while Edit View Group Order still lists every option.
    const config = {
      ...boardEnumOptionsConfig,
      hiddenOptionIds: [],
    };
    if (isNodelistBoard) {
      return orderRelationOptions(relationOptions, config);
    }
    return resolveEnumOptionsForField(boardFieldDef, language, config);
  }, [boardEnumOptionsConfig, boardFieldDef, isNodelistBoard, language, relationOptions]);

  const normalizedBoardOrder = useMemo(() => (
    normalizeBoardOrder(boardOrder, boardFieldKey)
  ), [boardOrder, boardFieldKey]);

  const boardData = useMemo(() => {
    const groups = new Map();
    const columns = [];

    normalizedOptions.forEach((option) => {
      const value = option?.value != null ? String(option.value) : '';
      if (!value) return;
      if (!groups.has(value)) groups.set(value, []);
      columns.push({
        key: value,
        optionId: String(option?.id || value || '').trim() || null,
        label: getOptionLabel(option, language),
        color: option?.color || '',
        iconKey: String(option?.iconKey || '').trim(),
      });
    });

    const unassigned = [];
    const unknown = [];

    nodes.forEach((node) => {
      const overrideKey = typeof node?.key === 'string' ? node.key : '';
      const hasOverride = overrideKey && Object.prototype.hasOwnProperty.call(fieldValueOverrides, overrideKey);
      const rawValue = hasOverride
        ? fieldValueOverrides[overrideKey]
        : boardFieldDef
          ? resolveFieldValue(node, boardFieldDef)
          : (dataSectionKey ? node?.[dataSectionKey]?.[boardFieldKey] : null);
      const value = resolveBoardCellValue(rawValue, boardFieldDef);

      if (!value) {
        unassigned.push(node);
        return;
      }

      if (groups.has(value)) {
        groups.get(value).push(node);
        return;
      }

      const valueLower = String(value).toLowerCase();
      let matchedKey = null;
      for (const key of groups.keys()) {
        if (String(key).toLowerCase() === valueLower) {
          matchedKey = key;
          break;
        }
      }
      if (matchedKey) {
        groups.get(matchedKey).push(node);
        return;
      }

      unknown.push(node);
    });

    if (unassigned.length) {
      groups.set(UNASSIGNED_KEY, unassigned);
      columns.push({ key: UNASSIGNED_KEY, optionId: null, label: 'Unassigned', color: '', iconKey: '' });
    }

    if (unknown.length) {
      groups.set(OTHER_KEY, unknown);
      columns.push({ key: OTHER_KEY, optionId: null, label: 'Other', color: '', iconKey: '' });
    }

    columns.forEach((column) => {
      const columnKey = column?.key;
      if (!columnKey || !groups.has(columnKey)) return;
      const items = groups.get(columnKey) || [];
      const orderedKeys = normalizedBoardOrder.columns?.[columnKey] || [];
      groups.set(columnKey, reconcileOrderedItems(items, orderedKeys));
    });

    return { columns, groups };
  }, [
    nodes,
    dataSectionKey,
    boardFieldKey,
    normalizedOptions,
    language,
    boardFieldDef,
    resolveFieldValue,
    fieldValueOverrides,
    normalizedBoardOrder,
  ]);

  return {
    boardFieldKey,
    fields,
    boardFieldDef,
    normalizedOptions,
    normalizedBoardOrder,
    boardData,
    resolveFieldValue,
    isNodelistBoard,
  };
};

export default useBoardGroups;
